import React from 'react';
import { CheckCircle, X, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const ComparisonTable = () => {
  const { t } = useTranslation();
  
  const comparisonData = {
    headers: [
      t('comparisonTable.headers.feature'),
      t('comparisonTable.headers.minhaComposicao'),
      t('comparisonTable.headers.bibliotecaNacional'),
      t('comparisonTable.headers.cartorio')
    ],
    rows: [
      {
        feature: '🏛️ Registro Automático na Biblioteca Nacional',
        minhaComposicao: '✅ Sim (Gratuito a cada 120 dias)',
        bibliotecaNacional: '❌ Manual (Você faz por conta)',
        cartorio: '❌ Não disponível',
        highlight: true
      },
      {
        feature: '🎯 Dupla Proteção (Digital + BN)',
        minhaComposicao: '✅ Sim (Certificado + Registro BN)',
        bibliotecaNacional: '❌ Apenas registro BN',
        cartorio: '❌ Apenas cartório',
        highlight: true
      },
      {
        feature: t('comparisonTable.features.process'),
        minhaComposicao: t('comparisonTable.values.onlineOnly'),
        bibliotecaNacional: t('comparisonTable.values.onlineGovBr'),
        cartorio: t('comparisonTable.values.inPersonOrMail')
      },
      {
        feature: t('comparisonTable.features.deliveryTime'),
        minhaComposicao: t('comparisonTable.values.immediate'),
        bibliotecaNacional: t('comparisonTable.values.up90Days'),
        cartorio: t('comparisonTable.values.5to15Days')
      },
      {
        feature: t('comparisonTable.features.costPerWork'),
        minhaComposicao: t('comparisonTable.values.freeUp5'),
        bibliotecaNacional: t('comparisonTable.values.r40to80'),
        cartorio: t('comparisonTable.values.r206plus')
      },
      {
        feature: t('comparisonTable.features.legalValidity'),
        minhaComposicao: t('comparisonTable.values.yesPrivate'),
        bibliotecaNacional: t('comparisonTable.values.yesPublic'),
        cartorio: t('comparisonTable.values.yesPublicFaith')
      },
      {
        feature: t('comparisonTable.features.digitalCertificate'),
        minhaComposicao: t('comparisonTable.values.p7sPdfJson'),
        bibliotecaNacional: t('comparisonTable.values.certificatePdf'),
        cartorio: t('comparisonTable.values.physicalAta')
      },
      {
        feature: t('comparisonTable.features.electronicSignature'),
        minhaComposicao: t('comparisonTable.values.icpGovBr'),
        bibliotecaNacional: t('comparisonTable.values.govBr'),
        cartorio: t('comparisonTable.values.notApplicable')
      },
      {
        feature: t('comparisonTable.features.timestamp'),
        minhaComposicao: t('comparisonTable.values.rfc3161'),
        bibliotecaNacional: t('comparisonTable.values.yes'),
        cartorio: t('comparisonTable.values.ataDate')
      },
      {
        feature: t('comparisonTable.features.cryptoHash'),
        minhaComposicao: t('comparisonTable.values.sha256'),
        bibliotecaNacional: t('comparisonTable.values.yes'),
        cartorio: t('comparisonTable.values.no')
      },
      {
        feature: t('comparisonTable.features.publicQuery'),
        minhaComposicao: t('comparisonTable.values.yesPlatform'),
        bibliotecaNacional: t('comparisonTable.values.yesGovBr'),
        cartorio: t('comparisonTable.values.no')
      },
      {
        feature: t('comparisonTable.features.coauthorship'),
        minhaComposicao: t('comparisonTable.values.yesDigitalSig'),
        bibliotecaNacional: t('comparisonTable.values.simpleDeclaration'),
        cartorio: t('comparisonTable.values.collectiveAta')
      },
      {
        feature: t('comparisonTable.features.autoScore'),
        minhaComposicao: t('comparisonTable.values.yesIncluded'),
        bibliotecaNacional: t('comparisonTable.values.no'),
        cartorio: t('comparisonTable.values.no')
      },
      {
        feature: t('comparisonTable.features.storage'),
        minhaComposicao: t('comparisonTable.values.1to2Years'),
        bibliotecaNacional: t('comparisonTable.values.permanent'),
        cartorio: t('comparisonTable.values.permanent')
      },
      {
        feature: t('comparisonTable.features.ecadIntegration'),
        minhaComposicao: t('comparisonTable.values.assistedGuide'),
        bibliotecaNacional: t('comparisonTable.values.no'),
        cartorio: t('comparisonTable.values.no')
      },
      {
        feature: t('comparisonTable.features.licenseMarketplace'),
        minhaComposicao: t('comparisonTable.values.yes'),
        bibliotecaNacional: t('comparisonTable.values.no'),
        cartorio: t('comparisonTable.values.no')
      },
      {
        feature: t('comparisonTable.features.googleIndexing'),
        minhaComposicao: t('comparisonTable.values.yesAutomatic'),
        bibliotecaNacional: t('comparisonTable.values.no'),
        cartorio: t('comparisonTable.values.no')
      },
      {
        feature: t('comparisonTable.features.plagiarismProtection'),
        minhaComposicao: t('comparisonTable.values.yesAiRecognition'),
        bibliotecaNacional: t('comparisonTable.values.no'),
        cartorio: t('comparisonTable.values.no')
      },
      {
        feature: t('comparisonTable.features.publicPromotion'),
        minhaComposicao: t('comparisonTable.values.yesSeoOptimized'),
        bibliotecaNacional: t('comparisonTable.values.limited'),
        cartorio: t('comparisonTable.values.no')
      }
    ]
  };
  
  const getIcon = (value) => {
    const lowerValue = value.toLowerCase();
    if (lowerValue.includes('sim') || lowerValue.includes('yes') || lowerValue.includes('sí') || lowerValue.includes('sì') ||
        lowerValue.includes('imediato') || lowerValue.includes('immediate') || lowerValue.includes('inmediato') || lowerValue.includes('immediato') ||
        lowerValue.includes('gratuito') || lowerValue.includes('free') || lowerValue.includes('gratis') ||
        lowerValue.includes('automatico') || lowerValue.includes('automatic') || lowerValue.includes('automático')) {
      return <CheckCircle className="w-5 h-5 text-green-600" />;
    }
    if (lowerValue.includes('nao') || lowerValue.includes('não') || lowerValue.includes('no')) {
      return <X className="w-5 h-5 text-red-500" />;
    }
    return <AlertCircle className="w-5 h-5 text-yellow-600" />;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
            {comparisonData.headers.map((header, index) => (
              <th key={index} className="px-6 py-4 text-left font-semibold border-r border-white/20 last:border-r-0">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {comparisonData.rows.map((row, index) => (
            <motion.tr
              key={index}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              viewport={{ once: true }}
              className={`${
                row.highlight 
                  ? 'bg-gradient-to-r from-yellow-50 to-green-50 border-l-4 border-yellow-500' 
                  : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
              } hover:bg-blue-50 transition-colors`}
            >
              <td className={`px-6 py-4 ${row.highlight ? 'font-bold text-gray-900 text-lg' : 'font-semibold text-gray-900'} border-r border-gray-200`}>
                {row.feature}
              </td>
              <td className={`px-6 py-4 border-r border-gray-200 ${row.highlight ? 'font-bold' : ''}`}>
                <div className="flex items-center space-x-2">
                  {getIcon(row.minhaComposicao)}
                  <span className={`${row.highlight ? 'text-green-700 font-bold text-base' : 'font-medium text-blue-600'}`}>
                    {row.minhaComposicao}
                  </span>
                </div>
              </td>
              <td className="px-6 py-4 border-r border-gray-200">
                <div className="flex items-center space-x-2">
                  {getIcon(row.bibliotecaNacional)}
                  <span className="text-gray-700">{row.bibliotecaNacional}</span>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center space-x-2">
                  {getIcon(row.cartorio)}
                  <span className="text-gray-700">{row.cartorio}</span>
                </div>
              </td>
            </motion.tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ComparisonTable;
